import { FC } from 'react';
import { Form, Button } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import styled from '@emotion/styled';
import { useLoginMutation } from 'pages/auth/Login/useLoginMutation';
import { Title } from 'components/Typography';
import { Input } from 'components/form/Input';
import { FormItem } from 'components/form/FormItem';
import { PasswordInput } from 'components/form/PasswordInput';
import { FormattedMessage, useIntl } from 'react-intl';
import { LoginDTO, loginSchema } from 'pages/auth/authModels';
import { useNavigate } from 'react-router-dom';
import { authRoutes } from 'routes';
import { AuthCard } from 'pages/auth/components/AuthCard';
import { AppLogo } from 'components/AppLogo';

const StyledTitle = styled(Title)({
  marginBottom: '2rem',
  marginTop: '2.5rem',
  textAlign: 'center',
});

const StyledFormItem = styled(FormItem)({
  marginBottom: '1.5rem',
});

const ForgotPwdWrapper = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '1.25rem',
  marginBottom: '1rem',
});

const LogoContainer = styled.div({
  display: 'flex',
  justifyContent: 'center',
});

export const LoginPage: FC = () => {
  const methods = useForm<LoginDTO>({
    resolver: zodResolver(loginSchema),
  });
  const { mutate, isLoading } = useLoginMutation();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  return (
    <AuthCard>
      <LogoContainer>
        <AppLogo />
      </LogoContainer>
      <StyledTitle>
        <FormattedMessage id="auth.accountLogin" />
      </StyledTitle>
      <FormProvider {...methods}>
        <Form
          onFinish={methods.handleSubmit((data) => mutate(data))}
          layout="vertical"
        >
          <StyledFormItem
            name="username"
            label={formatMessage({ id: 'field.email' })}
          >
            <Input name="username" autoComplete="email" />
          </StyledFormItem>
          <StyledFormItem name="password">
            <PasswordInput name="password" autoComplete="current-password" />
          </StyledFormItem>
          <ForgotPwdWrapper>
            <Button
              type="link"
              onClick={() => navigate(authRoutes.resetPassword)}
            >
              <FormattedMessage id="auth.forgotPassword" />
            </Button>
          </ForgotPwdWrapper>
          <Button
            block
            type="primary"
            htmlType="submit"
            loading={isLoading}
            disabled={!methods.formState.isValid}
          >
            <FormattedMessage id="auth.login" />
          </Button>
        </Form>
      </FormProvider>
    </AuthCard>
  );
};
